import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  EyeIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  PhoneIcon,
  CalendarIcon,
  HeartIcon,
  ShieldIcon,
  AwardIcon
} from "lucide-react";
import BookAppointmentModal from "../BookAppointmentModal";
import Breadcrumb from "../Breadcrumb";
import { contentService } from "../../services";
import { openWhatsApp } from "../../lib/utils";

const OphthalmologyIndex = () => {
  const [ophthalmologyTreatments, setOphthalmologyTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch ophthalmology treatments from API
  useEffect(() => {
    const fetchTreatments = async () => {
      try {
        setLoading(true);
        const treatmentsData = await contentService.getAllOphthalmologyTreatments();
        setOphthalmologyTreatments(treatmentsData);
      } catch (err) {
        console.error('Error fetching ophthalmology treatments:', err);
        setError('Failed to load ophthalmology treatments');
        // Fallback to mock data
        setOphthalmologyTreatments([
    {
      name: 'cataract-surgery',
      title: 'Cataract Surgery',
      description: 'Advanced cataract removal with premium intraocular lens implantation for crystal clear vision.',
      icon: EyeIcon,
      color: 'bg-blue-500',
      popular: true
    },
    {
      name: 'lasik-eye-surgery',
      title: 'LASIK Eye Surgery',
      description: 'Laser vision correction to eliminate dependence on glasses and contact lenses.',
      icon: EyeIcon,
      color: 'bg-green-500',
      popular: true
    },
    {
      name: 'femto-lasik-surgery',
      title: 'Femto LASIK Surgery',
      description: 'Bladeless LASIK using femtosecond laser technology for enhanced safety and precision.',
      icon: EyeIcon,
      color: 'bg-cyan-500',
      popular: true
    },
    {
      name: 'glaucoma-surgery',
      title: 'Glaucoma Treatment',
      description: 'Comprehensive glaucoma management to prevent vision loss and preserve eyesight.',
      icon: EyeIcon,
      color: 'bg-purple-500',
      popular: false
    },
    {
      name: 'diabetic-retinopathy',
      title: 'Diabetic Retinopathy',
      description: 'Specialized treatment for diabetes-related eye complications and retinal damage.',
      icon: EyeIcon,
      color: 'bg-red-500',
      popular: false
    },
    {
      name: 'retinal-detachment',
      title: 'Retinal Detachment',
      description: 'Emergency retinal surgery to restore vision and prevent permanent blindness.',
      icon: EyeIcon,
      color: 'bg-gray-900',
      popular: false
    },
    {
      name: 'vitrectomy-surgery',
      title: 'Vitrectomy Surgery',
      description: 'Advanced vitreoretinal surgery for complex eye conditions and retinal disorders.',
      icon: EyeIcon,
      color: 'bg-indigo-500',
      popular: false
    },
    {
      name: 'icl-surgery',
      title: 'ICL Surgery',
      description: 'Implantable Contact Lens surgery for high refractive errors and thin corneas.',
      icon: EyeIcon,
      color: 'bg-teal-500',
      popular: false
    },
    {
      name: 'smile-lasik-surgery',
      title: 'SMILE LASIK',
      description: 'Minimally invasive small lenticule extraction for severe myopia correction.',
      icon: EyeIcon,
      color: 'bg-pink-500',
      popular: false
    },
    {
      name: 'prk-lasik-surgery',
      title: 'PRK LASIK',
      description: 'Surface laser treatment ideal for patients with thin corneas or active lifestyles.',
      icon: EyeIcon,
      color: 'bg-yellow-500',
      popular: false
    },
    {
      name: 'contoura-vision',
      title: 'Contoura Vision',
      description: 'Topography-guided LASIK for personalized vision correction and better outcomes.',
      icon: EyeIcon,
      color: 'bg-emerald-500',
      popular: false
    },
    {
      name: 'phaco-surgery',
      title: 'Phaco Surgery',
      description: 'Phacoemulsification cataract surgery with ultrasonic technology for quick recovery.',
      icon: EyeIcon,
      color: 'bg-violet-500',
      popular: false
    },
    {
      name: 'squint-surgery',
      title: 'Squint Surgery',
      description: 'Corrective surgery for eye alignment disorders and improved binocular vision.',
      icon: EyeIcon,
      color: 'bg-rose-500',
      popular: false
    }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTreatments();
  }, []);

  const formatDiseaseName = (name) => {
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const popularTreatments = ophthalmologyTreatments.filter(treatment => treatment.popular);
  const otherTreatments = ophthalmologyTreatments.filter(treatment => !treatment.popular);

  const benefits = [
    "Advanced femtosecond laser technology",
    "Experienced LASIK surgeons with 10+ years expertise",
    "Bladeless and minimally invasive procedures",
    "98% or higher success rate",
    "Free post-operative care and consultations",
    "No-Cost EMI payment options available"
  ];

  const stats = [
    { number: "50K+", label: "Eye Surgeries", icon: EyeIcon },
    { number: "98%", label: "Success Rate", icon: StarIcon },
    { number: "15+", label: "Years Experience", icon: AwardIcon },
    { number: "24/7", label: "Emergency Care", icon: ShieldIcon }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading ophthalmology treatments...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { label: "Ophthalmology" }
            ]} 
          />
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <EyeIcon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
              <span className="text-blue-600">Ophthalmology</span> Treatments
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4 sm:px-0">
              Advanced eye care with cutting-edge laser technology and minimally invasive surgical treatments. 
              From LASIK vision correction to complex retinal surgeries, we provide comprehensive ophthalmology services with world-class expertise.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-4 sm:px-0">
              <Button size="lg" className="bg-gray-900 hover:bg-gray-600 text-sm sm:text-base" onClick={() => openWhatsApp("9311720045", "Hi, I am contacting you for treatment from TheLivCure site.")}>
                <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                <span className="hidden sm:inline">Call: +91 93117 20045</span>
                <span className="sm:hidden">Call Now</span>
              </Button>
              <BookAppointmentModal>
                <Button size="lg" variant="outline" className="text-sm sm:text-base">
                  <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="hidden sm:inline">Book Free Eye Checkup</span>
                  <span className="sm:hidden">Book Checkup</span>
                </Button>
              </BookAppointmentModal>
            </div>

            {/* Stats temporarily disabled per request */}
            {false && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto px-4 sm:px-0">
              {stats.map((stat, index) => (
                <Card key={index} className="text-center">
                  <CardContent className="p-3 sm:p-4 lg:p-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                      <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
                    </div>
                    <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1">{stat.number}</div>
                    <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Popular Treatments */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Popular Eye Treatments</h2>
            <p className="text-lg text-gray-600">Most sought-after ophthalmology procedures</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {popularTreatments.map((treatment, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow group">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 ${treatment.color} rounded-lg flex items-center justify-center`}>
                      <treatment.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <Badge className="bg-orange-100 text-orange-800 text-xs sm:text-sm">Popular</Badge>
                  </div>
                  
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{treatment.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">{treatment.description}</p>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                    <Link to={`/ophthalmology/${treatment.name}`}>
                      <Button className="bg-blue-600 hover:bg-blue-700 group-hover:bg-blue-700 text-sm sm:text-base w-full sm:w-auto">
                        Learn More
                        <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                      </Button>
                    </Link>
                    <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-500">
                      <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>30-60 min</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* All Treatments */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">All Eye Treatments</h2>
            <p className="text-lg text-gray-600">Comprehensive ophthalmology services for all eye conditions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherTreatments.map((treatment, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow group">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className={`w-10 h-10 ${treatment.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <treatment.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{treatment.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">{treatment.description}</p>
                      <Link to={`/ophthalmology/${treatment.name}`}>
                        <Button variant="outline" size="sm" className="group-hover:bg-blue-50 group-hover:text-blue-600">
                          View Details
                          <ArrowRightIcon className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why Choose TheLivCure for Eye Treatment?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We are India's leading ophthalmology center with state-of-the-art technology 
                and experienced eye surgeons dedicated to preserving and improving your vision.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <Card className="text-center">
                <CardContent className="p-6">
                  <HeartIcon className="w-8 h-8 text-red-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900">99%</div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <ClockIcon className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900">Same Day</div>
                  <div className="text-sm text-gray-600">Discharge</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <ShieldIcon className="w-8 h-8 text-green-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900">100%</div>
                  <div className="text-sm text-gray-600">Safe Procedures</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <AwardIcon className="w-8 h-8 text-purple-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900">100%</div>
                  <div className="text-sm text-gray-600">All Insurance Covered</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Improve Your Vision?</h2>
          <p className="text-xl mb-8 opacity-90">
            Book a free consultation with our expert ophthalmologists and discover the best treatment for your eyes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gray-900 hover:bg-gray-600" onClick={() => openWhatsApp("9311720045", "Hi, I am contacting you for treatment from TheLivCure site.")}>
              <PhoneIcon className="w-5 h-5 mr-2" />
              Call Now: +91 93117 20045
            </Button>
            <BookAppointmentModal>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
                <CalendarIcon className="w-5 h-5 mr-2" />
                Book Free Eye Checkup
              </Button>
            </BookAppointmentModal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OphthalmologyIndex;